'use client';

/* eslint-disable react-hooks/exhaustive-deps */

import React, { MouseEventHandler, useEffect, useState } from 'react';

import './sessionCalendar.css';

import { Box, Button, Divider, Flex, VStack, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { CalendarDay, DayPicker } from 'react-day-picker';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import { CustomText } from '@components/Custom-text';
import { GlobalConfigTypes } from '@enums/index';
import {
    AvailabilityForDateResponse,
    AvailabilityListResponse,
    BlackoutDateResponse,
} from '@modules/availability/types';
import { useGetSessionIntervalQuery } from '@modules/find-mentor/api';
import { SlotWithId } from '@modules/find-mentor/types';
import { getAvailableHoursForCurrentDate, isBlackoutDay } from '@utils/helpers';

type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece];

type SessionCalendarProps = {
    availabilityList: AvailabilityListResponse;
    blackoutDates: BlackoutDateResponse[];
    setSlotsForDay: React.Dispatch<React.SetStateAction<SlotWithId[]>>;
    setSelectedDate: (value: Value) => void;
    selectedDate: Value;
    slug: string;
    getIntervals: (values: { username: string; date: string }) => void;
    availabilityForDay: AvailabilityForDateResponse;
};

const SessionCalendar = ({
    availabilityList,
    blackoutDates,
    setSlotsForDay,
    setSelectedDate,
    selectedDate,
    slug,
    getIntervals,
    availabilityForDay,
}: SessionCalendarProps) => {
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const { data: sessionIntervalData, isLoading: isIntervalLoading } = useGetSessionIntervalQuery(
        GlobalConfigTypes.SessionIntervalInMinutes
    );

    const daysWithSlots: string[] = availabilityList
        ?.filter(item => item.isChecked)
        .map(item => item.weekDay as string);

    useEffect(() => {
        const dateFilter = dayjs().format('YYYY-MM-DD');
        getIntervals({
            username: slug,
            date: dateFilter,
        });
    }, []);

    const onChange = (val: any) => {
        setSelectedDate(val);
        const dateFilter = dayjs(val).format('YYYY-MM-DD');
        if (isBlackoutDay(val, blackoutDates)) {
            setSlotsForDay([]);
            return;
        }
        getIntervals({
            username: slug,
            date: dateFilter,
        });
    };

    useEffect(() => {
        if (
            availabilityForDay?.availabilitySlots &&
            availabilityForDay.availabilitySlots?.length > 0 &&
            !isBlackoutDay(selectedDate as Date, blackoutDates)
        ) {
            const finalAvailableDays = getAvailableHoursForCurrentDate(
                availabilityForDay?.availabilitySlots,
                selectedDate as Date
            );
            setSlotsForDay(finalAvailableDays);
        } else {
            setSlotsForDay([]);
        }
    }, [availabilityForDay, selectedDate]);

    const isAvailableDay = (date: Date): boolean => {
        const dayOfWeek = dayjs(date).format('ddd').toUpperCase();
        const isDayAvailable = daysWithSlots && daysWithSlots.includes(dayOfWeek);
        const isFuture = date.getTime() >= new Date().setHours(0, 0, 0, 0);
        const isNotBlackout = !isBlackoutDay(date, blackoutDates);

        return isDayAvailable && isFuture && isNotBlackout;
    };

    const handleSelect = (date: Date) => {
        onChange(date);
    };

    return (
        <VStack gap={2} width={{ base: '100%', sm: '100%', md: '100%' }}>
            <CustomText color="primary.dark" weight={500} size="xl" lineHeight={5}>
                Schedule Your Session
            </CustomText>
            {!isIntervalLoading && (
                <CustomText
                    color="subText"
                    size="md"
                    lineHeight={4}
                    marginTop={1}
                    textAlign="center"
                >
                    Each 1:1 session is scheduled for {sessionIntervalData?.value} minutes
                </CustomText>
            )}

            <Divider border="border" my={4} borderWidth="0.5px" />

            <Box className="calendar-grid-wrapper" width="100%">
                {daysWithSlots && daysWithSlots.length > 0 ? (
                    <DayPicker
                        mode="single"
                        month={selectedMonth}
                        onMonthChange={setSelectedMonth}
                        selected={
                            Array.isArray(selectedDate) ? undefined : selectedDate ?? undefined
                        }
                        showOutsideDays={false}
                        startMonth={new Date()}
                        className="calendar-root"
                        classNames={{ day_hidden: 'day-hidden' }}
                        hideWeekdays
                        hidden={date => !isAvailableDay(date)}
                        components={{
                            Nav: ({
                                className,
                                ...props
                            }: {
                                className?: string;
                                nextMonth?: Date | undefined;
                                previousMonth?: Date | undefined;
                                onPreviousClick?: MouseEventHandler<HTMLButtonElement> | undefined;
                                onNextClick?: MouseEventHandler<HTMLButtonElement> | undefined;
                            } & React.HTMLAttributes<HTMLElement>) => (
                                <Flex justify="center" align="center" className={className}>
                                    <Button
                                        onClick={props.onPreviousClick}
                                        size="lg"
                                        variant="ghost"
                                    >
                                        <FiChevronLeft size={20} />
                                    </Button>
                                    <Text fontSize="18px" mx={4}>
                                        {selectedMonth.toLocaleDateString('en-US', {
                                            month: 'long',
                                            year: 'numeric',
                                        })}
                                    </Text>
                                    <Button onClick={props.onNextClick} size="lg" variant="ghost">
                                        <FiChevronRight size={20} />
                                    </Button>
                                </Flex>
                            ),

                            DayButton: ({ day }: { day: CalendarDay }) => {
                                const isSelected =
                                    selectedDate instanceof Date &&
                                    day.date.getFullYear() === selectedDate.getFullYear() &&
                                    day.date.getMonth() === selectedDate.getMonth() &&
                                    day.date.getDate() === selectedDate.getDate();

                                return (
                                    <Box
                                        onClick={() => handleSelect(day.date)}
                                        className={`day-button ${
                                            selectedDate instanceof Date &&
                                            selectedDate.getTime() === day.date.getTime()
                                                ? 'selected'
                                                : ''
                                        }`}
                                        sx={{
                                            bgColor: isSelected ? 'lightBlueBg' : 'white',
                                            border: '1px solid',
                                            borderColor: 'border',
                                            cursor: 'pointer',
                                            padding: '10px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: '64px',
                                            borderRadius: '6px',
                                        }}
                                    >
                                        <CustomText
                                            color={isSelected ? 'primary.light' : 'mainText'}
                                            classes="date"
                                            weight={500}
                                        >
                                            {day.date.getDate()}
                                        </CustomText>
                                        <CustomText
                                            color={isSelected ? 'primary.light' : 'subText'}
                                            textTransform="uppercase"
                                            classes="day"
                                            weight={400}
                                        >
                                            {day.date.toLocaleDateString('en-US', {
                                                weekday: 'short',
                                            })}
                                        </CustomText>
                                    </Box>
                                );
                            },

                            CaptionLabel: () => <div />,
                        }}
                    />
                ) : (
                    <Flex
                        direction="column"
                        align="center"
                        justify="flex-start"
                        minHeight="300px"
                        width="100%"
                    >
                        <Flex justify="center" align="center" mb={6}>
                            <Button
                                onClick={() => {
                                    const prevMonth = new Date(selectedMonth);
                                    prevMonth.setMonth(prevMonth.getMonth() - 1);
                                    if (prevMonth >= new Date()) {
                                        setSelectedMonth(prevMonth);
                                    }
                                }}
                                size="lg"
                                variant="ghost"
                                isDisabled={selectedMonth <= new Date()}
                            >
                                <FiChevronLeft size={20} />
                            </Button>
                            <Text fontSize="18px" mx={4}>
                                {selectedMonth.toLocaleDateString('en-US', {
                                    month: 'long',
                                    year: 'numeric',
                                })}
                            </Text>
                            <Button
                                onClick={() => {
                                    const nextMonth = new Date(selectedMonth);
                                    nextMonth.setMonth(nextMonth.getMonth() + 1);
                                    setSelectedMonth(nextMonth);
                                }}
                                size="lg"
                                variant="ghost"
                            >
                                <FiChevronRight size={20} />
                            </Button>
                        </Flex>
                        <CustomText color="subText" size="lg" textAlign="center" weight={400}>
                            There are no available dates.
                        </CustomText>
                    </Flex>
                )}
            </Box>
        </VStack>
    );
};

export default SessionCalendar;

export type { Value as SelectedDateType };
